import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, NumberInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Plus, CheckCircle, XCircle, Upload, Pencil, Trash2 } from "lucide-react";
import { fmtINR, fmtIN, fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

function StatusPill({ status }) {
    const map = {
        DRAFT: { tone: "slate", label: "Draft" },
        SUBMITTED: { tone: "amber", label: "Pending Approval" },
        APPROVED: { tone: "emerald", label: "Approved" },
        REJECTED: { tone: "red", label: "Rejected" },
    };
    const { tone, label } = map[status] || { tone: "slate", label: status };
    return <Pill tone={tone}>{label}</Pill>;
}

export default function MachineRates() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [machines, setMachines] = useState([]);
    const [error, setError] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // draft rate being edited
    const emptyForm = {
        machine_id: "", plant_id: "",
        power_kw: 0, power_rate_per_kwh: 8.5,
        operator_cost_per_hr: 250, maintenance_per_hr: 75,
        depreciation_per_hr: 150, overhead_per_hr: 100,
    };
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const canCreate = hasRole(user, "CORPORATE_ADMIN", "PLANT_MANAGER");
    const isAdmin = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        Promise.all([api.get("/machine-rates"), api.get("/machines")])
            .then(([rt, m]) => { setRows(rt.data); setMachines(m.data); })
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const machineMap = useMemo(() => Object.fromEntries(machines.map((m) => [m.id, m])), [machines]);
    const calculated = useMemo(() => {
        const f = form;
        return (parseFloat(f.power_kw)||0) * (parseFloat(f.power_rate_per_kwh)||0)
             + (parseFloat(f.operator_cost_per_hr)||0)
             + (parseFloat(f.maintenance_per_hr)||0)
             + (parseFloat(f.depreciation_per_hr)||0)
             + (parseFloat(f.overhead_per_hr)||0);
    }, [form]);

    const onMachineSelect = (id) => {
        const m = machineMap[id];
        setForm({ ...form, machine_id: id, plant_id: m?.plant_id || "" });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = Object.fromEntries(
                Object.entries(form).map(([k, v]) => [k, k.includes("_id") ? v : parseFloat(v) || 0])
            );
            if (editing) {
                // Machine + plant are locked once the draft exists — only cost inputs are mutable.
                const { machine_id: _m, plant_id: _p, ...costFields } = payload;
                await api.put(`/machine-rates/${editing.id}`, costFields);
                toast.success("Draft rate updated");
            } else {
                await api.post("/machine-rates", payload);
                toast.success("Draft machine rate created");
            }
            setOpen(false);
            setEditing(null);
            setForm(emptyForm);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    // Edit behaves differently depending on the row's approval state:
    //  • DRAFT / REJECTED — user is still iterating → PUT-edit the existing row
    //  • SUBMITTED / APPROVED — locked by workflow → open the modal in "new
    //    draft" mode pre-filled with the current values, so the user creates
    //    a revision without mutating an audited record.
    const openEdit = (r) => {
        const inPlace = r.status === "DRAFT" || r.status === "REJECTED";
        setEditing(inPlace ? r : null);
        setForm({
            machine_id: r.machine_id || "", plant_id: r.plant_id || "",
            power_kw: r.power_kw ?? 0,
            power_rate_per_kwh: r.power_rate_per_kwh ?? 8.5,
            operator_cost_per_hr: r.operator_cost_per_hr ?? 0,
            maintenance_per_hr: r.maintenance_per_hr ?? 0,
            depreciation_per_hr: r.depreciation_per_hr ?? 0,
            overhead_per_hr: r.overhead_per_hr ?? 0,
        });
        if (!inPlace) {
            toast.info(`Rate is ${r.status.toLowerCase()} — opening a new draft revision with these values.`);
        }
        setOpen(true);
    };

    const submitForApproval = async (id) => {
        try { await api.post(`/machine-rates/${id}/submit`); toast.success("Submitted for approval"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const approve = async (id) => {
        try { await api.post(`/machine-rates/${id}/approve`); toast.success("Approved"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const reject = async (id) => {
        const reason = window.prompt("Reason for rejection:");
        if (!reason) return;
        try { await api.post(`/machine-rates/${id}/reject?reason=${encodeURIComponent(reason)}`); toast.success("Rejected"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (r) => {
        if (!window.confirm(`Delete this ${r.status.toLowerCase()} machine rate? This action cannot be undone.`)) return;
        try {
            await api.delete(`/machine-rates/${r.id}`);
            toast.success("Machine rate deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid={TID.machineRatesPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 4 · APPROVAL WORKFLOW"
                title="Machine Hourly Rates"
                subtitle="Plant Manager submits → Corporate Admin approves. Only approved rates are selectable in ZBC costings."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="machine-rates" label="Machine Rates" canImport={isAdmin} onImported={load} />
                        {canCreate && (
                            <CopperButton onClick={openAdd} data-testid={TID.machineRatesAddBtn}>
                                <Plus className="h-4 w-4" /> New Rate
                            </CopperButton>
                        )}
                    </div>
                }
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {rows && rows.length === 0 && (
                <EmptyState
                    icon={Upload}
                    title="No machine rates yet"
                    description={canCreate ? "Submit hourly rate inputs for your machines to get them approved for use in ZBCs." : "Plant managers haven't submitted rates yet."}
                />
            )}

            {rows && rows.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                        <table data-testid={TID.machineRatesTable} className="w-full table-rowhover">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60">
                                    <th className="eyebrow text-left py-2 px-4">Machine</th>
                                    <th className="eyebrow text-right py-2 px-4">Power (kW × ₹/kWh)</th>
                                    <th className="eyebrow text-right py-2 px-4">Operator</th>
                                    <th className="eyebrow text-right py-2 px-4">Maint.</th>
                                    <th className="eyebrow text-right py-2 px-4">Deprec.</th>
                                    <th className="eyebrow text-right py-2 px-4">Overhead</th>
                                    <th className="eyebrow text-right py-2 px-4">Total ₹/hr</th>
                                    <th className="eyebrow text-left py-2 px-4">Status</th>
                                    <th className="eyebrow text-left py-2 px-4">Submitted</th>
                                    <th className="eyebrow text-right py-2 px-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const m = machineMap[r.machine_id];
                                    const canDeleteRow = (r.status === "DRAFT" || r.status === "REJECTED") && canCreate;
                                    return (
                                        <tr key={r.id} className="border-b border-slate-100">
                                            <td className="py-1.5 px-4 text-[13px]">
                                                <div className="font-mono font-semibold">{m?.code || "—"}</div>
                                                <div className="text-[11px] text-slate-500 truncate max-w-[180px]">{m?.name}</div>
                                            </td>
                                            <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-right">{fmtIN(r.power_kw)} × {fmtINR(r.power_rate_per_kwh)}</td>
                                            <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-right">{fmtINR(r.operator_cost_per_hr, 0)}</td>
                                            <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-right">{fmtINR(r.maintenance_per_hr, 0)}</td>
                                            <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-right">{fmtINR(r.depreciation_per_hr, 0)}</td>
                                            <td className="py-1.5 px-4 text-[12px] font-mono tabular-nums text-right">{fmtINR(r.overhead_per_hr, 0)}</td>
                                            <td className="py-1.5 px-4 text-[13px] font-mono tabular-nums text-right font-semibold text-[hsl(var(--copper))]">{fmtINR(r.calculated_rate)}</td>
                                            <td className="py-1.5 px-4"><StatusPill status={r.status} /></td>
                                            <td className="py-1.5 px-4 text-[11px] text-slate-500">
                                                {r.submitted_at ? (<>{r.submitted_by_name}<br/>{fmtRelative(r.submitted_at)}</>) : "—"}
                                            </td>
                                            <td className="py-1.5 px-4 text-right">
                                                <div className="flex justify-end gap-1.5">
                                                    {/* Edit button is visible on EVERY row for admins/plant managers.
                                                        Behaviour by status:
                                                        - DRAFT / REJECTED → in-place edit (backend allows it)
                                                        - SUBMITTED / APPROVED → opens a fresh DRAFT pre-filled
                                                          with these values (a revision), preserving audit trail */}
                                                    {canCreate && (
                                                        <SlateButton
                                                            onClick={() => openEdit(r)}
                                                            variant="outline"
                                                            className="h-7 px-2.5 text-xs"
                                                            data-testid={`machine-rate-edit-${r.id}`}
                                                            title={
                                                                r.status === "DRAFT" || r.status === "REJECTED"
                                                                    ? "Edit rate inputs"
                                                                    : `Create a new draft revision (current status: ${r.status})`
                                                            }
                                                        >
                                                            <Pencil className="h-3 w-3" /> Edit
                                                        </SlateButton>
                                                    )}
                                                    {r.status === "DRAFT" && canCreate && (
                                                        <SlateButton onClick={() => submitForApproval(r.id)} className="h-7 px-2.5 text-xs" data-testid={TID.machineRateSubmit(r.id)}>
                                                            Submit
                                                        </SlateButton>
                                                    )}
                                                    {r.status === "SUBMITTED" && isAdmin && (
                                                        <>
                                                            <SlateButton onClick={() => approve(r.id)} variant="outline" className="h-7 px-2.5 text-xs" data-testid={TID.machineRateApprove(r.id)}>
                                                                <CheckCircle className="h-3 w-3" /> Approve
                                                            </SlateButton>
                                                            <SlateButton onClick={() => reject(r.id)} variant="outline" className="h-7 px-2.5 text-xs">
                                                                <XCircle className="h-3 w-3" />
                                                            </SlateButton>
                                                        </>
                                                    )}
                                                    {canDeleteRow && (
                                                        <SlateButton
                                                            onClick={() => remove(r)}
                                                            variant="ghost"
                                                            className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                            title="Delete this draft/rejected rate"
                                                            data-testid={`machine-rate-delete-${r.id}`}
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </SlateButton>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Draft Rate" : "New Machine Rate"} size="xl"
                footer={<>
                    <SlateButton variant="outline" onClick={() => setOpen(false)}>Cancel</SlateButton>
                    <CopperButton onClick={submit} disabled={submitting || !form.machine_id}>
                        {submitting ? "Saving…" : editing ? "Save Changes" : "Save Draft"}
                    </CopperButton>
                </>}
            >
                <form onSubmit={submit} className="space-y-5">
                    <Field label="Machine" required helper={editing ? "Machine is locked once the draft exists" : "Only machines you can manage are shown"}>
                        <Select value={form.machine_id} onChange={(e) => onMachineSelect(e.target.value)} required disabled={!!editing}>
                            <option value="">Select machine…</option>
                            {machines.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                        </Select>
                    </Field>

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Connected Power (kW)" required>
                            <NumberInput value={form.power_kw} onChange={(e) => setForm({ ...form, power_kw: e.target.value })} step="0.1" required />
                        </Field>
                        <Field label="Power Rate (₹/kWh)" required>
                            <NumberInput value={form.power_rate_per_kwh} onChange={(e) => setForm({ ...form, power_rate_per_kwh: e.target.value })} step="0.01" required />
                        </Field>
                        <Field label="Operator Cost (₹/hr)" required>
                            <NumberInput value={form.operator_cost_per_hr} onChange={(e) => setForm({ ...form, operator_cost_per_hr: e.target.value })} step="1" required />
                        </Field>
                        <Field label="Maintenance (₹/hr)" required>
                            <NumberInput value={form.maintenance_per_hr} onChange={(e) => setForm({ ...form, maintenance_per_hr: e.target.value })} step="1" required />
                        </Field>
                        <Field label="Depreciation (₹/hr)" required>
                            <NumberInput value={form.depreciation_per_hr} onChange={(e) => setForm({ ...form, depreciation_per_hr: e.target.value })} step="1" required />
                        </Field>
                        <Field label="Overhead (₹/hr)" required>
                            <NumberInput value={form.overhead_per_hr} onChange={(e) => setForm({ ...form, overhead_per_hr: e.target.value })} step="1" required />
                        </Field>
                    </div>

                    <div className="rounded-md border border-[hsl(var(--copper))]/30 bg-[hsl(var(--copper-soft))] p-4 flex items-baseline justify-between">
                        <div>
                            <div className="eyebrow text-slate-600">CALCULATED HOURLY RATE</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">Power + Operator + Maint + Deprec + OH</div>
                        </div>
                        <div className="font-display text-3xl font-semibold text-[hsl(var(--copper))] font-mono tabular-nums animate-calc-pulse" key={calculated}>
                            {fmtINR(calculated)}<span className="text-sm text-slate-500 ml-1">/hr</span>
                        </div>
                    </div>
                </form>
            </Modal>
        </div>
    );
}